import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { TaskRow } from "../components/TaskRow";
import { UndoToast } from "../components/UndoToast";
import { ApiError, type FamilyApi } from "../lib/api";
import { groupOccurrencesForToday, type TodayTask } from "../lib/task-model";
import type { ParentMember, SessionResponse, TaskOccurrence } from "../lib/types";

interface TodayPageProps {
  api: FamilyApi;
  offline: boolean;
  today?: string;
  session?: Extract<SessionResponse, { authenticated: true }>;
}

interface LoadedData {
  members: ParentMember[];
  tasks: TaskOccurrence[];
}

interface UndoState {
  original: TodayTask;
  completionEventId: string;
  completedVersion: number;
  deadlineMs: number;
}

export function TodayPage({ api, offline, today = taipeiToday(), session }: TodayPageProps) {
  const [data, setData] = useState<LoadedData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [workingId, setWorkingId] = useState<string | null>(null);
  const [undo, setUndo] = useState<UndoState | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [members, occurrences] = await Promise.all([
        api.getMembers(),
        api.getOccurrences({ status: "OPEN" }),
      ]);
      setData({ members: members.data, tasks: occurrences.data });
    } catch (caught) {
      setError(messageFor(caught));
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  const complete = async (task: TodayTask) => {
    const startedAt = Date.now();
    setWorkingId(task.occurrenceId);
    setError(null);
    try {
      const result = await api.completeOccurrence(task.occurrenceId, task.version);
      const receivedAt = Date.now();
      setData((current) =>
        current
          ? { ...current, tasks: current.tasks.filter((item) => item.occurrenceId !== task.occurrenceId) }
          : current,
      );
      if (result.undo) {
        const serverWindow = Date.parse(result.undo.expiresAt) - Date.parse(result.serverTime);
        const observedHalfTrip = Math.max(0, receivedAt - startedAt) / 2;
        setUndo({
          original: task,
          completionEventId: result.undo.completionEventId,
          completedVersion: result.task.version,
          deadlineMs: receivedAt + Math.max(0, serverWindow - observedHalfTrip),
        });
      }
    } catch (caught) {
      setError(messageFor(caught));
    } finally {
      setWorkingId(null);
    }
  };

  const restore = async () => {
    if (!undo) return;
    setWorkingId(undo.original.occurrenceId);
    setError(null);
    try {
      const result = await api.undoCompletion(undo.completionEventId, undo.completedVersion);
      setData((current) =>
        current
          ? {
              ...current,
              tasks: [
                ...current.tasks,
                { ...undo.original, status: "OPEN", version: result.task.version, completedAt: null },
              ],
            }
          : current,
      );
      setUndo(null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 410) {
        setUndo(null);
        void load();
      } else {
        setError(messageFor(caught));
      }
    } finally {
      setWorkingId(null);
    }
  };

  const groups = data ? groupOccurrencesForToday(data.tasks, data.members, today) : [];
  const taskCount = groups.reduce((sum, group) => sum + group.tasks.length, 0);

  return (
    <section className="page-stack" aria-labelledby="today-title">
      <header className="page-heading page-heading-row">
        <div>
          <p className="date-line">{formatTaipeiDate(today)}</p>
          <h1 id="today-title">今天</h1>
          <p>{taskCount > 0 ? `還有 ${taskCount} 件，一件一件來。` : "今天的清單很乾淨。"}</p>
        </div>
        <Link className="button button-primary compact-add" to="/tasks/new">＋ 新增</Link>
      </header>

      {offline && data ? <p className="stale-note">離線資料，連線後才能勾選</p> : null}
      {error ? <InlineError message={error} onRetry={load} /> : null}
      {!data ? <TaskSkeleton /> : null}
      {data ? (
        <div className="family-groups">
          {groups.map((group) => (
            <section className="family-group" key={group.key} data-member={group.member?.slug ?? "household"}>
              <header className="group-heading">
                <span className="member-color" aria-hidden="true" />
                <h2>{group.label}</h2>
                <span className="task-count">{group.tasks.length}</span>
              </header>
              {group.tasks.length ? (
                <ul className="task-list">
                  {group.tasks.map((task) => (
                    <TaskRow
                      key={task.occurrenceId}
                      task={task}
                      disabled={offline}
                      pending={workingId === task.occurrenceId}
                      canManage={canManageTask(session, task)}
                      onComplete={complete}
                    />
                  ))}
                </ul>
              ) : (
                <p className="group-empty">今天沒有待辦</p>
              )}
            </section>
          ))}
        </div>
      ) : null}

      {undo ? (
        <UndoToast
          title={undo.original.title}
          deadlineMs={undo.deadlineMs}
          pending={workingId === undo.original.occurrenceId}
          onUndo={() => void restore()}
          onExpire={() => setUndo(null)}
        />
      ) : null}
    </section>
  );
}

function canManageTask(
  session: Extract<SessionResponse, { authenticated: true }> | undefined,
  task: TaskOccurrence,
) {
  return (
    !session ||
    session.member.role === "PARENT" ||
    (task.assignment.type === "MEMBER" &&
      task.assignment.memberId === session.member.memberId)
  );
}

function InlineError({ message, onRetry }: { message: string; onRetry: () => Promise<void> }) {
  return (
    <div className="notice notice-error inline-error" role="alert">
      <span>{message}</span>
      <button type="button" onClick={() => void onRetry()}>重新整理</button>
    </div>
  );
}

function TaskSkeleton() {
  return (
    <div className="task-skeleton" aria-busy="true" aria-label="正在載入今日待辦">
      <span /><span /><span />
    </div>
  );
}

function taipeiToday() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Taipei",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

function formatTaipeiDate(localDate: string) {
  const date = new Date(`${localDate}T12:00:00+08:00`);
  return new Intl.DateTimeFormat("zh-TW", {
    timeZone: "Asia/Taipei",
    month: "long",
    day: "numeric",
    weekday: "long",
  }).format(date);
}

function messageFor(error: unknown) {
  return error instanceof ApiError ? error.message : "待辦暫時載入失敗，請再試一次。";
}
